import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/gossip";

const navItems = [
  { label: "首页", to: "/" },
  { label: "复盘档案", to: "/events" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const catRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/events?q=${encodeURIComponent(q)}` : "/events");
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "bg-background-50/95 border-background-200 backdrop-blur"
          : "bg-background-50 border-background-100"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 md:h-[72px] items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
            <span className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-chat-smile-3-fill text-lg md:text-xl"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-bold text-lg md:text-xl text-foreground-950 tracking-tight">
                91吃瓜网
              </span>
              <span className="font-label text-[10px] md:text-[11px] text-secondary-600 tracking-[0.2em] mt-0.5">
                深扒档案室
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-7 font-label text-sm">
            {navItems.slice(0, 2).map((item) => {
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`relative py-1 transition-colors cursor-pointer ${
                    active
                      ? "text-primary-600 font-medium"
                      : "text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  {item.label}
                  {active && (
                    <span className="absolute -bottom-0.5 left-0 right-0 h-0.5 rounded-full bg-primary-500"></span>
                  )}
                </Link>
              );
            })}

            <div className="relative" ref={catRef}>
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className="flex items-center gap-1 py-1 text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
              >
                主题分类
                <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? "rotate-180" : ""}`}></i>
              </button>
              {catOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-[520px] rounded-lg border border-background-200 bg-background-50 p-3 grid grid-cols-2 gap-1">
                  {categories.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/category/${c.slug}`}
                      className="flex items-start gap-2.5 rounded-md p-2.5 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <span className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md bg-accent-100 text-accent-800">
                        <i className={`${c.icon} text-base`}></i>
                      </span>
                      <span className="flex flex-col">
                        <span className="font-label text-sm text-foreground-900">{c.name}</span>
                        <span className="font-label text-[11px] text-foreground-500 leading-snug mt-0.5">
                          {c.desc}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`relative py-1 transition-colors cursor-pointer ${
                location.pathname === "/about"
                  ? "text-primary-600 font-medium"
                  : "text-foreground-700 hover:text-primary-600"
              }`}
            >
              关于我们
            </Link>
          </nav>

          {/* Search + CTA */}
          <div className="flex items-center gap-2 md:gap-3">
            <form onSubmit={submitSearch} className="hidden lg:flex items-center relative">
              <i className="ri-search-line absolute left-3 text-foreground-400 text-sm"></i>
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索事件 / 人物"
                aria-label="搜索事件或人物"
                className="w-44 xl:w-56 rounded-md border border-background-300 bg-background-100 py-2 pl-9 pr-3 font-label text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </form>
            <Link
              to="/events"
              className="hidden md:inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 font-label text-sm text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-circle-line text-base"></i>
              查看全部复盘
            </Link>
            <button
              type="button"
              aria-label="打开导航菜单"
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-md border border-background-300 text-foreground-800 cursor-pointer"
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-3-line"} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile panel */}
      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 pb-5 pt-4">
          <form onSubmit={submitSearch} className="relative mb-4">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索事件 / 人物"
              aria-label="搜索事件或人物"
              className="w-full rounded-md border border-background-300 bg-background-100 py-2.5 pl-9 pr-3 font-label text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </form>
          <div className="flex flex-col gap-1">
            {navItems.slice(0, 2).map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="rounded-md px-3 py-2.5 font-label text-sm text-foreground-800 hover:bg-background-100 cursor-pointer"
              >
                {item.label}
              </Link>
            ))}
          </div>
          <p className="mt-4 mb-2 px-3 font-label text-[11px] tracking-widest text-foreground-400">
            主题分类
          </p>
          <div className="grid grid-cols-2 gap-1">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="flex items-center gap-2 rounded-md px-3 py-2 font-label text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
              >
                <i className={`${c.icon} text-base text-accent-700`}></i>
                {c.name}
              </Link>
            ))}
          </div>
          <Link
            to="/about"
            className="mt-2 block rounded-md px-3 py-2.5 font-label text-sm text-foreground-800 hover:bg-background-100 cursor-pointer"
          >
            关于我们
          </Link>
        </div>
      )}
    </header>
  );
}